import React, { useState, useRef, useEffect } from "react";
import {
  IoIosArrowRoundDown,
  IoIosArrowRoundUp,
  IoIosArrowDown,
  IoIosArrowBack,
  IoMdClose,
} from "react-icons/io";
import { FaFilter, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { IoCodeSharp } from "react-icons/io5";
import { BiCollapseHorizontal, BiExpandHorizontal } from "react-icons/bi";
import {
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { format } from "date-fns";
import axios from "axios";
import { createPortal } from "react-dom";

const DOLLAR_ACCOUNT_IDS = [7948821642, 1176825945];
const DEFAULT_FALLBACK_DISPLAY = "–";
const DEFAULT_LOCALE = "en-IN";

const PERCENT_MULTI_OPTIONS = ["Less than 0", "25+", "50+", "75+"];

const currencyInfoMap = Object.freeze({
  AED: { name: "United Arab Emirates Dirham", symbol: "د.إ" },
  AFN: { name: "Afghan Afghani", symbol: "؋" },
  ALL: { name: "Albanian Lek", symbol: "L" },
  AMD: { name: "Armenian Dram", symbol: "֏" },
  ANG: { name: "Netherlands Antillean Guilder", symbol: "ƒ" },
  AOA: { name: "Angolan Kwanza", symbol: "Kz" },
  ARS: { name: "Argentine Peso", symbol: "$" },
  AUD: { name: "Australian Dollar", symbol: "$" },
  AWG: { name: "Aruban Florin", symbol: "ƒ" },
  AZN: { name: "Azerbaijani Manat", symbol: "₼" },
  BAM: { name: "Bosnia-Herzegovina Convertible Mark", symbol: "KM" },
  BBD: { name: "Barbadian Dollar", symbol: "$" },
  BDT: { name: "Bangladeshi Taka", symbol: "৳" },
  BGN: { name: "Bulgarian Lev", symbol: "лв" },
  BHD: { name: "Bahraini Dinar", symbol: ".د.ب" },
  BIF: { name: "Burundian Franc", symbol: "FBu" },
  BMD: { name: "Bermudan Dollar", symbol: "$" },
  BND: { name: "Brunei Dollar", symbol: "$" },
  BOB: { name: "Bolivian Boliviano", symbol: "Bs." },
  BRL: { name: "Brazilian Real", symbol: "R$" },
  BSD: { name: "Bahamian Dollar", symbol: "$" },
  BTN: { name: "Bhutanese Ngultrum", symbol: "Nu." },
  BWP: { name: "Botswanan Pula", symbol: "P" },
  BYN: { name: "Belarusian Ruble", symbol: "Br" },
  BZD: { name: "Belize Dollar", symbol: "BZ$" },
  CAD: { name: "Canadian Dollar", symbol: "$" },
  CDF: { name: "Congolese Franc", symbol: "FC" },
  CHF: { name: "Swiss Franc", symbol: "CHF" },
  CLP: { name: "Chilean Peso", symbol: "$" },
  CNY: { name: "Chinese Yuan", symbol: "¥" },
  COP: { name: "Colombian Peso", symbol: "$" },
  CRC: { name: "Costa Rican Colón", symbol: "₡" },
  CUP: { name: "Cuban Peso", symbol: "$" },
  CVE: { name: "Cape Verdean Escudo", symbol: "$" },
  CZK: { name: "Czech Koruna", symbol: "Kč" },
  DJF: { name: "Djiboutian Franc", symbol: "Fdj" },
  DKK: { name: "Danish Krone", symbol: "kr" },
  DOP: { name: "Dominican Peso", symbol: "RD$" },
  DZD: { name: "Algerian Dinar", symbol: "د.ج" },
  EGP: { name: "Egyptian Pound", symbol: "E£" },
  ERN: { name: "Eritrean Nakfa", symbol: "Nfk" },
  ETB: { name: "Ethiopian Birr", symbol: "Br" },
  EUR: { name: "Euro", symbol: "€" },
  FJD: { name: "Fijian Dollar", symbol: "$" },
  FKP: { name: "Falkland Islands Pound", symbol: "£" },
  GBP: { name: "British Pound Sterling", symbol: "£" },
  GEL: { name: "Georgian Lari", symbol: "₾" },
  GGP: { name: "Guernsey Pound", symbol: "£" },
  GHS: { name: "Ghanaian Cedi", symbol: "₵" },
  GIP: { name: "Gibraltar Pound", symbol: "£" },
  GMD: { name: "Gambian Dalasi", symbol: "D" },
  GNF: { name: "Guinean Franc", symbol: "FG" },
  GTQ: { name: "Guatemalan Quetzal", symbol: "Q" },
  GYD: { name: "Guyanaese Dollar", symbol: "$" },
  HKD: { name: "Hong Kong Dollar", symbol: "$" },
  HNL: { name: "Honduran Lempira", symbol: "L" },
  HTG: { name: "Haitian Gourde", symbol: "G" },
  HUF: { name: "Hungarian Forint", symbol: "Ft" },
  IDR: { name: "Indonesian Rupiah", symbol: "Rp" },
  ILS: { name: "Israeli New Shekel", symbol: "₪" },
  IMP: { name: "Manx pound", symbol: "£" },
  INR: { name: "Indian Rupee", symbol: "₹" },
  IQD: { name: "Iraqi Dinar", symbol: "ع.د" },
  IRR: { name: "Iranian Rial", symbol: "﷼" },
  ISK: { name: "Icelandic Króna", symbol: "kr" },
  JEP: { name: "Jersey Pound", symbol: "£" },
  JMD: { name: "Jamaican Dollar", symbol: "J$" },
  JOD: { name: "Jordanian Dinar", symbol: "JD" },
  JPY: { name: "Japanese Yen", symbol: "¥" },
  KES: { name: "Kenyan Shilling", symbol: "KSh" },
  KGS: { name: "Kyrgystani Som", symbol: "с" },
  KHR: { name: "Cambodian Riel", symbol: "៛" },
  KMF: { name: "Comorian Franc", symbol: "CF" },
  KPW: { name: "North Korean Won", symbol: "₩" },
  KRW: { name: "South Korean Won", symbol: "₩" },
  KWD: { name: "Kuwaiti Dinar", symbol: "KD" },
  KYD: { name: "Cayman Islands Dollar", symbol: "$" },
  KZT: { name: "Kazakhstani Tenge", symbol: "₸" },
  LAK: { name: "Laotian Kip", symbol: "₭" },
  LBP: { name: "Lebanese Pound", symbol: "ل.ل" },
  LKR: { name: "Sri Lankan Rupee", symbol: "Rs" },
  LRD: { name: "Liberian Dollar", symbol: "$" },
  LSL: { name: "Lesotho Loti", symbol: "L" },
  LYD: { name: "Libyan Dinar", symbol: "LD" },
  MAD: { name: "Moroccan Dirham", symbol: "MAD" },
  MDL: { name: "Moldovan Leu", symbol: "L" },
  MGA: { name: "Malagasy Ariary", symbol: "Ar" },
  MKD: { name: "Macedonian Denar", symbol: "ден" },
  MMK: { name: "Myanma Kyat", symbol: "K" },
  MNT: { name: "Mongolian Tugrik", symbol: "₮" },
  MOP: { name: "Macanese Pataca", symbol: "MOP$" },
  MRU: { name: "Mauritanian Ouguiya", symbol: "UM" },
  MUR: { name: "Mauritian Rupee", symbol: "₨" },
  MVR: { name: "Maldivian Rufiyaa", symbol: "Rf" },
  MWK: { name: "Malawian Kwacha", symbol: "MK" },
  MXN: { name: "Mexican Peso", symbol: "$" },
  MYR: { name: "Malaysian Ringgit", symbol: "RM" },
  MZN: { name: "Mozambican Metical", symbol: "MT" },
  NAD: { name: "Namibian Dollar", symbol: "$" },
  NGN: { name: "Nigerian Naira", symbol: "₦" },
  NIO: { name: "Nicaraguan Córdoba", symbol: "C$" },
  NOK: { name: "Norwegian Krone", symbol: "kr" },
  NPR: { name: "Nepalese Rupee", symbol: "₨" },
  NZD: { name: "New Zealand Dollar", symbol: "$" },
  OMR: { name: "Omani Rial", symbol: "﷼" },
  PAB: { name: "Panamanian Balboa", symbol: "B/." },
  PEN: { name: "Peruvian Sol", symbol: "S/." },
  PGK: { name: "Papua New Guinean Kina", symbol: "K" },
  PHP: { name: "Philippine Peso", symbol: "₱" },
  PKR: { name: "Pakistani Rupee", symbol: "₨" },
  PLN: { name: "Polish Zloty", symbol: "zł" },
  PYG: { name: "Paraguayan Guarani", symbol: "₲" },
  QAR: { name: "Qatari Rial", symbol: "﷼" },
  RON: { name: "Romanian Leu", symbol: "lei" },
  RSD: { name: "Serbian Dinar", symbol: "дин." },
  RUB: { name: "Russian Ruble", symbol: "₽" },
  RWF: { name: "Rwandan Franc", symbol: "R₣" },
  SAR: { name: "Saudi Riyal", symbol: "﷼" },
  SBD: { name: "Solomon Islands Dollar", symbol: "$" },
  SCR: { name: "Seychellois Rupee", symbol: "₨" },
  SDG: { name: "Sudanese Pound", symbol: "ج.س." },
  SEK: { name: "Swedish Krona", symbol: "kr" },
  SGD: { name: "Singapore Dollar", symbol: "$" },
  SHP: { name: "Saint Helena Pound", symbol: "£" },
  SLE: { name: "Sierra Leonean Leone", symbol: "Le" },
  SOS: { name: "Somali Shilling", symbol: "S" },
  SRD: { name: "Surinamese Dollar", symbol: "$" },
  SSP: { name: "South Sudanese Pound", symbol: "£" },
  STN: { name: "São Tomé and Príncipe Dobra", symbol: "Db" },
  SVC: { name: "Salvadoran Colón", symbol: "$" },
  SYP: { name: "Syrian Pound", symbol: "£" },
  SZL: { name: "Swazi Lilangeni", symbol: "L" },
  THB: { name: "Thai Baht", symbol: "฿" },
  TJS: { name: "Tajikistani Somoni", symbol: "SM" },
  TMT: { name: "Turkmenistani Manat", symbol: "T" },
  TND: { name: "Tunisian Dinar", symbol: "د.ت" },
  TOP: { name: "Tongan Paʻanga", symbol: "T$" },
  TRY: { name: "Turkish Lira", symbol: "₺" },
  TTD: { name: "Trinidad and Tobago Dollar", symbol: "TT$" },
  TWD: { name: "New Taiwan Dollar", symbol: "NT$" },
  TZS: { name: "Tanzanian Shilling", symbol: "TSh" },
  UAH: { name: "Ukrainian Hryvnia", symbol: "₴" },
  UGX: { name: "Ugandan Shilling", symbol: "USh" },
  USD: { name: "United States Dollar", symbol: "$" },
  UYU: { name: "Uruguayan Peso", symbol: "$U" },
  UZS: { name: "Uzbekistan Som", symbol: "soʻm" },
  VED: { name: "Venezuelan Digital Bolívar", symbol: "Bs.D" },
  VES: { name: "Venezuelan Bolívar Soberano", symbol: "Bs." },
  VND: { name: "Vietnamese Dong", symbol: "₫" },
  VUV: { name: "Vanuatu Vatu", symbol: "VT" },
  WST: { name: "Samoan Tala", symbol: "T" },
  XAF: { name: "CFA Franc BEAC", symbol: "FCFA" },
  XCD: { name: "East Caribbean Dollar", symbol: "$" },
  XCG: { name: "Caribbean Guilder", symbol: "Cg" },
  XDR: { name: "Special Drawing Rights", symbol: "XDR" },
  XOF: { name: "CFA Franc BCEAO", symbol: "CFA" },
  XPF: { name: "CFP Franc", symbol: "₣" },
  YER: { name: "Yemeni Rial", symbol: "﷼" },
  ZAR: { name: "South African Rand", symbol: "R" },
  ZMW: { name: "Zambian Kwacha", symbol: "ZK" },
  ZWG: { name: "Zimbabwe Gold", symbol: "ZiG" },
});

function getCurrencyInfo(currencyCode) {
  if (!currencyCode || typeof currencyCode !== "string") {
    return undefined;
  }
  const normalizedCode = currencyCode.toUpperCase();
  return currencyInfoMap[normalizedCode];
}

export const _formatNumber = (num, options = {}) => {
  const { fractionDigits } = options;
  const formatOptions = {};
  if (fractionDigits !== undefined) {
    formatOptions.minimumFractionDigits = fractionDigits;
    formatOptions.maximumFractionDigits = fractionDigits;
  }
  return Number(num).toLocaleString(
    getLocalCurrencyStringType(),
    formatOptions,
  );
};

export const getCurrencySymbolFromISO = (currencyCode) => {
  return getCurrencyInfo(currencyCode)?.symbol;
};

export const getCurrencySymbol = () => {
  try {
    const customerId = Number(localStorage.getItem("customer_id"));
    if (DOLLAR_ACCOUNT_IDS.includes(customerId)) {
      return "$";
    }
  } catch (error) {
    console.error("Failed to read customer_id from localStorage:", error);
  }

  if (localStorage.getItem("currency_symbol") !== undefined) {
    return localStorage.getItem("currency_symbol");
  }
  return "₹";
};

export const getCurrencyCodeFromSymbol = (symbol) => {
  for (const [code, info] of Object.entries(currencyInfoMap)) {
    if (info.symbol === symbol) {
      return code;
    }
  }
  return "USD";
};

export const getLocalCurrencyStringType = (
  symbol = localStorage.getItem("currency_symbol"),
) => {
  const currencyCode = getCurrencyCodeFromSymbol(symbol);
  const types = {
    INR: "en-IN",
    USD: "en-US",
  };

  return types[currencyCode] === undefined ? "en-US" : types[currencyCode];
};

export const displayNumericValue = (val, fractions = 0) => {
  if (!val || val === undefined || val === null) return val;

  return val.toLocaleString(getLocalCurrencyStringType(), {
    minimumFractionDigits: fractions,
    maximumFractionDigits: fractions,
  });
};

// Helper function to format any valid date string/object to YYYY-MM-DD
const formatDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return val; // Fallback if invalid date
  return d.toISOString().split("T")[0]; // Returns "YYYY-MM-DD"
};

export const renderTableCell = (col, row, active) => {
  const statusColorMap = {
    completed: "bg-green-500",
    enabled: "bg-green-700",
    paused: "bg-gray-500",
    removed: "bg-red-700",
    active: "bg-green-500",
  };

  // Reporting
  if (
    (col.key.includes("percentage_difference") ||
      row.is_difference ||
      col.type === "percentage-multi") &&
    col.type !== "string"
  ) {
    let formattedValue = _formatNumber(row[col.key], {
      fractionDigits: 2,
    });
    if (formattedValue === undefined) {
      return "";
    }
    if (typeof formattedValue === "string") {
      formattedValue = Number(formattedValue.replace(/[^0-9.-]+/g, ""));
    }
    return (
      <span className="flex flex-row justify-end items-center text-right">
        {formattedValue > 0 ? (
          <IoIosArrowRoundUp className="text-green-500 text-lg" />
        ) : formattedValue === 0 ? (
          ""
        ) : (
          <IoIosArrowRoundDown className="text-red-500 text-lg" />
        )}
        {formattedValue > 0
          ? `+${formattedValue}%`
          : formattedValue === 0
            ? `${formattedValue}%`
            : `${formattedValue}%`}
      </span>
    );
  }

  if (col.key === "remaining_monthly_budget") {
    const remaining = parseFloat(row[col.key]) || 0;
    const monthly = parseFloat(row.monthly_budget) || 0;
    const costs = parseFloat(row.costs) || 0;

    let percentage = 0;
    if (monthly > 0) {
      percentage = (remaining / monthly) * 100;
    } else if (remaining + costs > 0) {
      percentage = (remaining / (remaining + costs)) * 100;
    } else if (remaining > 0) {
      percentage = 50;
    }

    percentage = Math.max(0, Math.min(100, percentage));

    const textColor =
      percentage <= 0
        ? "text-red-600 font-semibold"
        : percentage < 25
          ? "text-amber-500 font-semibold"
          : "text-emerald-600 font-semibold";

    const barColor =
      percentage <= 0
        ? "bg-red-500"
        : percentage < 25
          ? "bg-amber-500"
          : "bg-emerald-500";

    const currSymbol = row.currency
      ? getCurrencySymbolFromISO(row.currency) || getCurrencySymbol()
      : getCurrencySymbol();
    const num = Number(remaining);
    const isNeg = num < 0;
    const absVal = isNeg ? Math.abs(num) : num;
    const formattedVal = `${isNeg ? "-" : ""}${currSymbol}${_formatNumber(absVal, { fractionDigits: 2 })}`;

    return (
      <div className="flex items-center justify-between gap-3 min-w-[180px] w-full">
        <div className="w-20 sm:w-24 bg-gray-200 rounded-full h-1.5 overflow-hidden shrink-0">
          <div
            className={`h-1.5 rounded-full ${barColor}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className={`whitespace-nowrap ${textColor}`}>{formattedVal}</span>
      </div>
    );
  }

  switch (col.key) {
    case "status": {
      const status = row.status?.toLowerCase();
      const colorClass = statusColorMap[status] || "bg-transparent";

      return (
        <td key={col.key} className="py-3 text-center">
          <div className="flex justify-center items-center">
            <div className={`w-3 h-3 ${colorClass} rounded-full`}></div>
          </div>
        </td>
      );
    }

    case "headlines":
      return <span dangerouslySetInnerHTML={{ __html: row[col.key] }} />;

    case "labels":
      return (
        <div className="flex flex-wrap gap-1">
          {row.labels?.length > 0 ? (
            row.labels.map((label, idx) => (
              <div
                key={idx}
                className="flex items-center bg-gray-100 rounded-md pr-6 py-1 text-sm font-medium"
              >
                <div
                  className="w-2 h-full mr-2 rounded-l-md"
                  style={{ backgroundColor: label.background_color || "#ccc" }}
                ></div>
                <span className="text-gray-800 w-6 flex">
                  {label.label_name}
                </span>
              </div>
            ))
          ) : (
            <span className="text-gray-400 text-xs"></span>
          )}
        </div>
      );

    default: {
      const value = row[col.key];

      // Compare
      if (
        typeof value === "object" &&
        value !== null &&
        value.value !== undefined
      ) {
        const { value: amount, percent: percentage } = value;
        const currentValue = row[col.key + "_current"];
        const previousValue = row[col.key + "_previous"];

        let displayValue = currentValue;

        if (col.compare_col_type != null) {
          if (col.compare_col_type === "change") {
            displayValue = amount;
          } else if (col.compare_col_type === "change_percentage") {
            displayValue = percentage;
          } else {
            displayValue = row[col.key + col.compare_col_type];
          }
        }

        let formattedDisplayValue;
        const isCurrencyType =
          col.type === "currency" &&
          col.compare_col_type !== "change_percentage";

        if (isCurrencyType) {
          const num = Number(displayValue);
          const isNeg = num < 0;
          const absVal = isNeg ? Math.abs(num) : num;
          formattedDisplayValue = `${isNeg ? "-" : ""}${getCurrencySymbol()}${_formatNumber(absVal, { fractionDigits: 2 })}`;
        } else {
          formattedDisplayValue = _formatNumber(displayValue, {
            fractionDigits: col.type === "number" ? 0 : 2,
          });
          if (
            col.type === "percentage" ||
            col.type === "percentage-multi" ||
            col.type === "float" ||
            col.compare_col_type === "change_percentage"
          ) {
            formattedDisplayValue += "%";
          }
        }

        return (
          <div>
            <span>{formattedDisplayValue}</span>
            {col.compare_col_type == null && percentage !== undefined && (
              <div
                style={{
                  color: "#6b7280",
                  fontSize: "0.875em",
                  lineHeight: "1.2",
                }}
              >
                ({percentage > 0 ? "+" : ""}
                {parseFloat(percentage).toFixed(1)}%)
              </div>
            )}
          </div>
        );
      }

      const returnVal = value ?? "";
      let cleanVal = 0;
      let parsedNum = 0;

      // Auto-detect date column by key name if col.type is not set
      const isDateCol =
        col.type === "date" || col.type === "week" || col.type === "month";
      // col.key.toLowerCase().includes("date") ||
      // col.key.endsWith("_at");

      if (isDateCol && returnVal) {
        return formatDate(returnVal);
      }

      switch (col.type) {
        case "date":
          return formatDate(returnVal);

        case "string": {
          // If it looks like a GMT/UTC full date string, format it
          if (
            typeof returnVal === "string" &&
            returnVal.includes("GMT") &&
            !isNaN(Date.parse(returnVal))
          ) {
            return formatDate(returnVal);
          }
          return returnVal ?? DEFAULT_FALLBACK_DISPLAY;
        }

        case "number":
          return _formatNumber(returnVal, { fractionDigits: 0 });

        case "float-no-perc": {
          cleanVal = returnVal;
          if (typeof returnVal === "string") {
            cleanVal = returnVal.replace(/[^0-9.-]+/g, "");
          }
          parsedNum = Number(cleanVal);
          if (isNaN(parsedNum)) parsedNum = 0;
          return `${parsedNum.toFixed(2)}`;
        }

        case "percentage-multi":
        case "float":
        case "percentage": {
          cleanVal = returnVal;
          if (typeof returnVal === "string") {
            cleanVal = returnVal.replace(/[^0-9.-]+/g, "");
          }
          parsedNum = Number(cleanVal);
          if (isNaN(parsedNum)) parsedNum = 0;
          return `${parsedNum.toFixed(2)}%`;
        }

        case "currency": {
          const num = Number(returnVal);
          const isNeg = num < 0;
          const absVal = isNeg ? Math.abs(num) : num;
          return `${isNeg ? "-" : ""}${getCurrencySymbol()}${_formatNumber(absVal, { fractionDigits: 2 })}`;
        }

        default:
          return returnVal ?? DEFAULT_FALLBACK_DISPLAY;
      }
    }
  }
};

export const renderTableTotal = (col, data, active) => {
  if (
    !col.calculation &&
    ![
      "number",
      "currency",
      "float",
      "percentage",
      "percentage-multi",
      "float-no-perc",
    ].includes(col.type)
  ) {
    return null;
  }

  const getSafeNumericValue = (row, key) => {
    const rawValue = row[key];

    if (
      typeof rawValue === "object" &&
      rawValue !== null &&
      rawValue.value !== undefined
    ) {
      const { value: amount, percent: percentage } = rawValue;
      const currentValue = row[key + "_current"];
      let displayValue = currentValue;

      if (col.compare_col_type != null) {
        if (col.compare_col_type === "change") {
          displayValue = amount;
        } else if (col.compare_col_type === "change_percentage") {
          displayValue = percentage;
        } else {
          displayValue = row[key + col.compare_col_type];
        }
      }

      return displayValue;
    }

    const numericValue =
      typeof rawValue === "object" && rawValue !== null
        ? rawValue.value
        : rawValue;
    return Number(numericValue) || 0;
  };

  const safeData = data ?? [];
  let total;

  if (col.calculation) {
    const { numerator, denominator, multiplier = 1 } = col.calculation;
    const totalNumerator = safeData.reduce(
      (sum, row) => sum + getSafeNumericValue(row, numerator),
      0,
    );
    const totalDenominator = safeData.reduce(
      (sum, row) => sum + getSafeNumericValue(row, denominator),
      0,
    );

    total =
      totalDenominator > 0
        ? (totalNumerator / totalDenominator) * multiplier
        : 0;

    if (col.compare_col_type === "change_percentage") {
      if (totalDenominator === 0) {
        total = 0;
      } else {
        const diff = totalNumerator - totalDenominator;
        total = (diff / Math.abs(totalDenominator)) * 100;
      }
    }
  } else {
    total = safeData.reduce(
      (sum, row) => sum + getSafeNumericValue(row, col.key),
      0,
    );
  }

  if (
    (col.key.includes("percentage_difference") ||
      col.type === "percentage-multi") &&
    col.type !== "string"
  ) {
    let formattedValue = _formatNumber(total, {
      fractionDigits: 2,
    });
    if (formattedValue === undefined) {
      return "";
    }
    if (typeof formattedValue === "string") {
      formattedValue = Number(formattedValue.replace(/[^0-9.-]+/g, ""));
    }
    return (
      <span className="flex flex-row justify-end items-center text-right">
        {formattedValue > 0 ? (
          <IoIosArrowRoundUp className="text-green-500 text-lg" />
        ) : formattedValue === 0 ? (
          ""
        ) : (
          <IoIosArrowRoundDown className="text-red-500 text-lg" />
        )}
        {formattedValue > 0
          ? `+${formattedValue}%`
          : formattedValue === 0
            ? `${formattedValue}%`
            : `${formattedValue}%`}
      </span>
    );
  }

  if (col.key === "remaining_monthly_budget") {
    const num = Number(total);
    const isNeg = num < 0;
    const absVal = isNeg ? Math.abs(num) : num;
    return (
      <span className="text-[#070a74] font-bold">
        {`${isNeg ? "-" : ""}${getCurrencySymbol()}${_formatNumber(absVal, { fractionDigits: 2 })}`}
      </span>
    );
  }

  switch (col.type) {
    case "number":
      return _formatNumber(total);
    case "currency": {
      const num = Number(total);
      const isNeg = num < 0;
      const absVal = isNeg ? Math.abs(num) : num;
      return `${isNeg ? "-" : ""}${getCurrencySymbol()}${_formatNumber(absVal, {
        fractionDigits: 2,
      })}`;
    }

    case "float-no-perc":
      return `${parseFloat(total).toFixed(2)}`;
    case "percentage-multi":
    case "percentage":
    case "float":
      return `${parseFloat(total).toFixed(2)}%`;

    default:
      return total;
  }
};

const WEEK_DISPLAY_MAP = {
  "Week 1": "First Week",
  "Week 2": "Second Week",
  "Week 3": "Third Week",
  "Week 4": "Last Week",
  "Week 5": "Fifth Week",
};

const getWeekDisplayLabel = (weekStr) => {
  return WEEK_DISPLAY_MAP[weekStr] || weekStr;
};

export const apiRequestBodyBuilder = (state, additionalParams = {}) => {
  let prevDates = {};
  let body;

  if (!state || state.length === 0 || !state[0]) {
    body = {
      ad_account_id: localStorage.getItem("ad_account_id"),
      email: localStorage.getItem("email"),
      customer_id: localStorage.getItem("customer_id"),
      project_id: localStorage.getItem("project_id"),
      platform: localStorage.getItem("platform"),
      ...additionalParams,
    };
    return body;
  }

  if (state.length > 1 && state[1]) {
    prevDates = {
      previous_start_date: format(state[1].startDate, "yyyy-MM-dd"),
      previous_end_date: format(state[1].endDate, "yyyy-MM-dd"),
    };
  }

  body = {
    ad_account_id: localStorage.getItem("ad_account_id"),
    platform: localStorage.getItem("platform"),
    email: localStorage.getItem("email"),
    customer_id: localStorage.getItem("customer_id"),
    project_id: localStorage.getItem("project_id"),
    start_date: format(state[0].startDate, "yyyy-MM-dd"),
    end_date: format(state[0].endDate, "yyyy-MM-dd"),
    ...prevDates,
    ...additionalParams,
  };
  return body;
};

export const formatSegmentData = (data, type) => {
  const formatDate = (dateString, type) => {
    switch (type) {
      case "week": {
        if (!dateString) return "";

        const parts = dateString.split("-");
        if (parts.length === 3) {
          const [y, m, d] = parts.map(Number);
          const startDate = new Date(y, m - 1, d);
          const endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6);

          const options = { month: "short", day: "numeric" };
          const startLabel = startDate.toLocaleDateString("en-US", options);
          const endLabel = endDate.toLocaleDateString("en-US", options);
          const year = endDate.getFullYear();

          if (startDate.getFullYear() !== endDate.getFullYear()) {
            return `${startLabel}, ${startDate.getFullYear()} – ${endLabel}, ${year}`;
          }

          return `${startLabel} – ${endLabel}, ${year}`;
        } else if (parts.length === 2) {
          const [monthStr, yearShort] = parts;
          return `${monthStr} 20${yearShort}`;
        }

        return dateString;
      }

      default:
        return dateString;
    }
  };

  const formattedData = data.map((row) => {
    const formattedSegment = Array.isArray(row.segments)
      ? row.segments.map((seg) => {
          return {
            ...seg,
            date: formatDate(seg["date"], type),
          };
        })
      : [];

    return {
      ...row,
      segments: formattedSegment,
    };
  });

  return formattedData;
};

const getStorageKey = (projectId) => `confidanto_cols_pref_${projectId}`;

export const fetchAndSavePriorityColumns = async (
  projectId = localStorage.getItem("project_id"),
) => {
  try {
    const res = await axios.post(
      "https://api.confidanto.com/project-columns/get",
      {
        project_id: projectId,
      },
    );

    const apiResponseData = res.data;
    const apiPriorityList =
      apiResponseData && apiResponseData.length > 0
        ? apiResponseData[0].columns
        : [];
    const visibilityMap = {};

    apiPriorityList.forEach((col) => {
      visibilityMap[col.key] = col.visible;
    });

    localStorage.setItem(
      getStorageKey(projectId),
      JSON.stringify(visibilityMap),
    );

    console.info(`Column preferences saved for Project ${projectId}`);
    return true;
  } catch (err) {
    console.error("Error fetching priority columns", err);
    return false;
  }
};

export const getPriorityColumnsFromStorage = (
  defaultColumns,
  projectId = localStorage.getItem("project_id"),
) => {
  try {
    const storageKey = getStorageKey(projectId);
    const storedData = localStorage.getItem(storageKey);

    if (!storedData) {
      console.warn("No local column preferences found, using defaults.");
      return defaultColumns;
    }

    const visibilityMap = JSON.parse(storedData);

    const updatedColumns = defaultColumns.map((col) => {
      if (!(col.isLocked || col.locked || col.sticky)) {
        if (visibilityMap.hasOwnProperty(col.key)) {
          return {
            ...col,
            visible: visibilityMap[col.key],
          };
        } else {
          return {
            ...col,
            visible: false,
          };
        }
      } else {
        return col;
      }
    });

    return updatedColumns;
  } catch (err) {
    console.error("Error reading columns from local storage", err);
    return defaultColumns;
  }
};

export const getSortableValueGLOBAL = (item, key) => {
  const value = item[key];
  const currentValue = item[key + "_current"];
  return typeof value === "object" &&
    value !== null &&
    value.value !== undefined
    ? currentValue
    : value;
};

export const getSortedDataGLOBAL = (sortConfig, data) => {
  if (!sortConfig.key) return data;

  return [...data].sort((a, b) => {
    let current_keyname = sortConfig.key;
    if (typeof a[sortConfig.key] === "object") {
      current_keyname = sortConfig.key + "_current";
    }
    if (a[current_keyname] < b[current_keyname]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[current_keyname] > b[current_keyname]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });
};

export const FilterComponent = ({
  columns,
  data,
  filterArray,
  setFilterArray,
  segmentType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [filterIdIncrement, setFilterIdIncrement] = useState(1);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedColumn(null);
    }
  }, [isOpen]);

  const uniqueCategories = Array.isArray(columns)
    ? [...new Set(columns.map((col) => col?.category).filter(Boolean))]
    : [];

  const handleApplyFilter = (filterObj) => {
    if (filterObj) {
      setFilterArray((prev) => [
        ...prev,
        { ...filterObj, id: filterIdIncrement },
      ]);
      setFilterIdIncrement((prev) => prev + 1);
      setIsOpen(false);
    }
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  const FilterSelectionView = () => {
    const [expandedCategory, setExpandedCategory] = useState(
      uniqueCategories[0] || null,
    );

    return (
      <div className="flex-1 p-5 space-y-6">
        <div className="space-y-2 pb-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">
            Columns
          </h3>
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            {uniqueCategories
              .filter((category) =>
                columns.some((c) => c.category === category && c.visible),
              )
              .map((category) => (
                <div
                  key={category}
                  className="border-b border-gray-100 last:border-0"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 text-sm font-medium text-gray-800 transition-colors focus:outline-none"
                    onClick={() =>
                      setExpandedCategory(
                        expandedCategory === category ? null : category,
                      )
                    }
                  >
                    {category}{" "}
                    <IoIosArrowDown
                      className={`transform transition-transform duration-200 ${
                        expandedCategory === category ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {expandedCategory === category && (
                    <div className="bg-slate-50/50 px-3 py-2 border-t border-gray-100">
                      {columns
                        .filter((c) => c.visible && c.category === category)
                        .map((col) => (
                          <button
                            key={col.id}
                            className="w-full flex items-center px-4 py-2.5 text-sm text-left text-gray-600 rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            onClick={() => setSelectedColumn(col)}
                          >
                            {col.title}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  const FilterInputView = () => {
    const column = selectedColumn;
    const [numericCondition, setNumericCondition] = useState("Greater");
    const [numericValue, setNumericValue] = useState("");
    const [stringValue, setStringValue] = useState("");
    const [stringCondition, setStringCondition] = useState("contains");
    const [selectedMultiOptions, setSelectedMultiOptions] = useState([]);
    const [uniqueOptions, setUniqueOptions] = useState([]);

    const isWeekSegmentFilter = column.key === "date" && segmentType === "week";

    useEffect(() => {
      if (column.type === "percentage-multi") {
        setUniqueOptions(PERCENT_MULTI_OPTIONS);
      } else if (column.type === "boolean") {
        const allValues = data.flatMap((item) => {
          const targetKey = isWeekSegmentFilter ? "week" : column.key;

          const topLevelValue = item[targetKey];
          const segmentValues = item.segments
            ? item.segments.map((segment) => segment[targetKey])
            : [];
          const displayProperty = column.displayProperty;

          const processedValues = [];
          const processValue = (val) => {
            if (Array.isArray(val)) {
              val.forEach((el) => processValue(el));
            } else if (
              displayProperty &&
              typeof val === "object" &&
              val !== null
            ) {
              processedValues.push(val[displayProperty]);
            } else if (val != null) {
              processedValues.push(val);
            }
          };

          processValue(topLevelValue);
          segmentValues.forEach(processValue);
          return processedValues;
        });

        const uniqueVals = [...new Set(allValues.filter((v) => v != null))];

        if (isWeekSegmentFilter) uniqueVals.sort();

        setUniqueOptions(uniqueVals);
      }
    }, [column, data, segmentType, isWeekSegmentFilter]);

    const handleApply = () => {
      let filterObj = null;
      switch (column.type) {
        case "percentage-multi": {
          if (selectedMultiOptions.length === 0)
            return alert("Please select at least one option.");
          filterObj = {
            title: column.title,
            key: column.key,
            condition: "is in range",
            sign: "in",
            type: "percentage-multi",
            value: selectedMultiOptions,
          };
          break;
        }
        case "number":
        case "currency":
        case "percentage":
        case "float": {
          if (!numericValue) return alert("Please enter a value.");
          const sign =
            numericCondition === "Greater"
              ? ">"
              : numericCondition === "Lower"
                ? "<"
                : "=";
          filterObj = {
            title: column.title,
            key: column.key,
            condition: numericCondition,
            sign,
            type: "number",
            value: numericValue,
          };
          break;
        }
        case "string": {
          if (!stringValue) return alert("Please enter a value.");
          filterObj = {
            title: column.title,
            key: column.key,
            condition: stringCondition,
            sign: stringCondition,
            type: "string",
            value: stringValue,
          };
          break;
        }
        case "boolean": {
          if (selectedMultiOptions.length === 0)
            return alert("Please select at least one option.");

          const finalKey = isWeekSegmentFilter ? "week" : column.key;
          const finalTitle = isWeekSegmentFilter ? "Week" : column.title;

          let finalValues = selectedMultiOptions;
          if (isWeekSegmentFilter) {
            finalValues = selectedMultiOptions.map((val) =>
              getWeekDisplayLabel(val),
            );
          }

          filterObj = {
            title: finalTitle,
            key: finalKey,
            condition: "is one of",
            sign: "in",
            type: "multi-select",
            value: finalValues,
          };
          break;
        }
        default:
          return;
      }
      handleApplyFilter(filterObj);
    };

    const handleClear = () => {
      setNumericValue("");
      setStringValue("");
      setSelectedMultiOptions([]);
      setStringCondition("contains");
    };

    const renderFilterInput = () => {
      switch (column.type) {
        case "percentage-multi":
          return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedMultiOptions}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.includes("all")) {
                      setSelectedMultiOptions(
                        selectedMultiOptions.length ===
                          PERCENT_MULTI_OPTIONS.length
                          ? []
                          : PERCENT_MULTI_OPTIONS.map(String),
                      );
                      return;
                    }
                    setSelectedMultiOptions(value);
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected.length) return "Select Ranges...";
                    return selected.join(", ");
                  }}
                  MenuProps={{ style: { zIndex: 100000 } }}
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={
                        PERCENT_MULTI_OPTIONS.length > 0 &&
                        selectedMultiOptions.length ===
                          PERCENT_MULTI_OPTIONS.length
                      }
                      indeterminate={
                        selectedMultiOptions.length > 0 &&
                        selectedMultiOptions.length <
                          PERCENT_MULTI_OPTIONS.length
                      }
                    />
                    <ListItemText
                      primary={
                        selectedMultiOptions.length > 0
                          ? "Deselect All"
                          : "Select All"
                      }
                      primaryTypographyProps={{
                        fontWeight: "600",
                      }}
                    />
                  </MenuItem>
                  {PERCENT_MULTI_OPTIONS.map((option) => (
                    <MenuItem key={option} value={String(option)}>
                      <Checkbox
                        checked={selectedMultiOptions.includes(String(option))}
                      />
                      <ListItemText primary={option} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          );
        case "number":
        case "currency":
        case "percentage":
        case "float":
          return (
            <div className="flex items-center space-x-2">
              <select
                value={numericCondition}
                onChange={(e) => setNumericCondition(e.target.value)}
                className="w-1/3 border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              >
                <option value="Greater">&gt;</option>
                <option value="Lower">&lt;</option>
                <option value="Equal">=</option>
              </select>
              <input
                type="number"
                value={numericValue}
                onChange={(e) => setNumericValue(e.target.value)}
                placeholder="e.g. 100"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              />
            </div>
          );
        case "string":
          return (
            <div className="flex items-center space-x-2">
              <select
                value={stringCondition}
                onChange={(e) => setStringCondition(e.target.value)}
                className="w-1/3 border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              >
                <option value="contains">Contains</option>
                <option value="not contains">Not Contains</option>
              </select>
              <input
                type="text"
                value={stringValue}
                onChange={(e) => setStringValue(e.target.value)}
                placeholder="Enter text..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              />
            </div>
          );
        case "boolean":
          return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedMultiOptions}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.includes("all")) {
                      setSelectedMultiOptions(
                        selectedMultiOptions.length === uniqueOptions.length
                          ? []
                          : uniqueOptions.map(String),
                      );
                      return;
                    }
                    setSelectedMultiOptions(value);
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected.length) return "Select Options...";
                    if (isWeekSegmentFilter) {
                      return selected
                        .map((val) => getWeekDisplayLabel(val))
                        .join(", ");
                    }
                    return selected.join(", ");
                  }}
                  MenuProps={{ style: { zIndex: 100000 } }}
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={
                        uniqueOptions.length > 0 &&
                        selectedMultiOptions.length === uniqueOptions.length
                      }
                      indeterminate={
                        selectedMultiOptions.length > 0 &&
                        selectedMultiOptions.length < uniqueOptions.length
                      }
                    />
                    <ListItemText
                      primary={
                        selectedMultiOptions.length > 0
                          ? "Deselect All"
                          : "Select All"
                      }
                      primaryTypographyProps={{
                        fontWeight: "600",
                      }}
                    />
                  </MenuItem>
                  {uniqueOptions.map((option) => (
                    <MenuItem key={option} value={String(option)}>
                      <Checkbox
                        checked={selectedMultiOptions.includes(String(option))}
                      />
                      <ListItemText
                        primary={
                          isWeekSegmentFilter
                            ? getWeekDisplayLabel(String(option))
                            : String(option)
                        }
                      />
                    </MenuItem>
                  ))}
                  {uniqueOptions.length === 0 && (
                    <MenuItem disabled>No options available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </div>
          );
        default:
          return (
            <p className="text-sm text-gray-500">Unsupported filter type.</p>
          );
      }
    };

    return (
      <div className="flex-1 flex flex-col p-6 bg-white">
        <div className="space-y-4 flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Configure condition
          </label>
          {renderFilterInput()}
        </div>

        <div className="flex space-x-3 mt-8 pt-5 border-t border-gray-100">
          <button
            onClick={handleClear}
            className="flex-1 bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            className="flex-[2] bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Apply Filter
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex justify-start items-start w-full">
      <div className="relative" ref={popoverRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-gray-700 text-sm font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 space-x-2"
        >
          <FaFilter className="text-gray-500" />
          <span> Filter</span>
          {filterArray.length > 0 && (
            <span className="absolute -top-2 -right-2 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
              {filterArray.length}
            </span>
          )}
        </button>

        {isOpen &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6"
              onClick={stopPropagation}
              onMouseDown={stopPropagation}
            >
              <div
                className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity cursor-pointer"
                onClick={() => setIsOpen(false)}
              ></div>

              <div className="relative w-full max-w-md bg-white sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden transform transition-all duration-200 ease-out animate-in fade-in zoom-in-95">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white shrink-0">
                  <div className="flex items-center space-x-3">
                    {selectedColumn && (
                      <button
                        onClick={() => setSelectedColumn(null)}
                        className="text-gray-400 hover:text-gray-800 transition-colors p-1.5 -ml-1.5 rounded-full hover:bg-gray-100"
                      >
                        <IoIosArrowBack size={20} />
                      </button>
                    )}
                    <h2 className="text-lg font-semibold text-gray-800">
                      {!selectedColumn ? "Filter by" : selectedColumn.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-700 transition-colors p-1.5 -mr-1.5 rounded-full hover:bg-gray-100"
                  >
                    <IoMdClose size={20} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto bg-slate-50 flex flex-col">
                  {selectedColumn ? (
                    <FilterInputView />
                  ) : (
                    <FilterSelectionView />
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

const checkValue = (cellValue, filter) => {
  if (filter.type === "percentage-multi") {
    const val = parseFloat(
      cellValue != null && typeof cellValue === "object"
        ? cellValue.value
        : cellValue,
    );
    if (isNaN(val)) return false;

    return filter.value.some((opt) => {
      if (opt === "Less than 0") return val < 0;
      if (opt === "25+") return val >= 25;
      if (opt === "50+") return val >= 50;
      if (opt === "75+") return val >= 75;
      return false;
    });
  }

  if (filter.type === "string") {
    const filterValueLower = String(filter.value).toLowerCase();
    const valueStr = String(
      cellValue != null && typeof cellValue === "object"
        ? cellValue.value
        : cellValue,
    ).toLowerCase();

    if (filter.condition === "contains")
      return valueStr.includes(filterValueLower);
    if (filter.condition === "not contains")
      return !valueStr.includes(filterValueLower);
  } else if (
    filter.type === "number" ||
    filter.type === "currency" ||
    filter.type === "percentage"
  ) {
    const numVal = parseFloat(
      typeof cellValue === "object" ? cellValue?.value : cellValue,
    );
    const filterVal = parseFloat(filter.value);
    if (isNaN(numVal) || isNaN(filterVal)) return false;

    if (filter.sign === ">") return numVal > filterVal;
    if (filter.sign === "<") return numVal < filterVal;
    if (filter.sign === "=") return numVal === filterVal;
  } else if (filter.type === "multi-select") {
    if (cellValue == null) return false;

    if (filter.key === "week") {
      const displayCellVal = getWeekDisplayLabel(String(cellValue));
      return filter.value.some((opt) => String(opt) === displayCellVal);
    }

    return filter.value.some((opt) => String(opt) === String(cellValue));
  }

  return true;
};

export const getFilteredData = (data, filterArray) => {
  if (!filterArray || filterArray.length === 0) return data;

  return data.reduce((acc, row) => {
    const filteredSegments = (row.segments || []).filter((segment) => {
      return filterArray.every((filter) => {
        if (segment[filter.key] !== undefined) {
          return checkValue(segment[filter.key], filter);
        }
        if (row[filter.key] !== undefined) {
          return checkValue(row[filter.key], filter);
        }
        return false;
      });
    });

    const parentMatches = filterArray.every((filter) => {
      if (row[filter.key] !== undefined) {
        return checkValue(row[filter.key], filter);
      }
      return false;
    });

    if (filteredSegments.length > 0) {
      acc.push({ ...row, segments: filteredSegments });
    } else if (parentMatches && (!row.segments || row.segments.length === 0)) {
      acc.push(row);
    }

    return acc;
  }, []);
};

export const AddTableFilterComponent = ({
  onApplyStaticFilter,
  filterRef,
  showFilterDropdown,
  setShowFilterDropdown,
  appliedFilters,
  handleRemoveFilter,
  selectedFilter,
  setSelectedFilter,
  columns,
  data,
  campaignNameInput,
  setCampaignNameInput,
  onApplyCampaignFilter,
  groups,
  selectedGroups,
  setSelectedGroups,
  onApplyGroupFilter,
  currentGroupType,
}) => {
  const uniqueCategory = [
    ...new Set(columns.map((col) => col.category).filter(Boolean)),
  ];

  const [staticFilterInput, setStaticFilterInput] = useState("");
  const [comparison, setComparison] = useState(">");
  const [selectedMultiOptions, setSelectedMultiOptions] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(
    uniqueCategory[0] || null,
  );

  useEffect(() => {
    if (showFilterDropdown) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showFilterDropdown]);

  const handleStaticFilterApply = (col) => {
    const isNum =
      col.keyType === "number" ||
      ["number", "currency", "percentage", "float"].includes(col.type);
    const isBool = col.type === "boolean" || col.keyType === "boolean";
    const isMultiPercent = col.type === "percentage-multi";

    if ((isBool || isMultiPercent) && selectedMultiOptions.length === 0) return;
    if (!isBool && !isMultiPercent && !staticFilterInput) return;

    const filter = {
      id: Date.now(),
      type: "static",
      key: col.key,
      keyType: isMultiPercent
        ? "percentage-multi"
        : isBool
          ? "boolean"
          : isNum
            ? "number"
            : "string",
      comparison:
        isMultiPercent || isBool ? "in" : isNum ? comparison : "contains",
      value:
        isBool || isMultiPercent ? selectedMultiOptions : staticFilterInput,
      label: `${col.title} ${
        isBool || isMultiPercent ? "is" : isNum ? comparison : "contains"
      } ${isBool || isMultiPercent ? selectedMultiOptions.join(", ") : staticFilterInput}`,
    };

    onApplyStaticFilter(filter);
    closePanel();
  };

  const getFilterTitle = (filter) => {
    if (!filter.label) return filter.key;
    if (filter.label.includes(" is ")) {
      return filter.label.split(" is ")[0];
    }
    if (filter.label.includes(": ")) {
      return filter.label.split(": ")[0];
    }
    if (filter.label.includes(" contains ")) {
      return filter.label.split(" contains ")[0];
    }
    return filter.label;
  };

  const handleRemoveSingleValue = (filter, valueToRemove) => {
    const remainingValues = filter.value.filter((val) => val !== valueToRemove);

    handleRemoveFilter(filter);

    if (remainingValues.length > 0) {
      const title = getFilterTitle(filter);
      const updatedFilter = {
        ...filter,
        id: Date.now(),
        value: remainingValues,
        label: `${title} is ${remainingValues.join(", ")}`,
      };
      onApplyStaticFilter(updatedFilter);
    }
  };

  const closePanel = () => {
    setShowFilterDropdown(false);
    setSelectedFilter(null);
    setStaticFilterInput("");
    setSelectedMultiOptions([]);
    setComparison(">");
  };

  const handleBack = () => {
    setSelectedFilter(null);
    setStaticFilterInput("");
    setSelectedMultiOptions([]);
  };

  const clearFormFields = () => {
    setStaticFilterInput("");
    setSelectedMultiOptions([]);
    setComparison(">");
    if (setCampaignNameInput) setCampaignNameInput("");
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  const ChevronDown = ({ isOpen }) => (
    <svg
      className={`w-4 h-4 text-gray-400 transform transition-transform duration-200 ${
        isOpen ? "rotate-180" : ""
      }`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );

  const ArrowLeft = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );

  const CloseIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  return (
    <div className="relative" ref={filterRef}>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          className="flex items-center px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-gray-700 text-sm font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <FaFilter className="mr-2 text-gray-500" /> Filter
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          {appliedFilters
            .flatMap((filter) => {
              const isMulti =
                filter.type === "static" && Array.isArray(filter.value);
              if (isMulti) {
                const title = getFilterTitle(filter);
                return filter.value.map((val) => ({
                  id: `${filter.id}-${val}`,
                  label: `${title} is ${val}`,
                  onRemove: () => handleRemoveSingleValue(filter, val),
                }));
              } else {
                return [
                  {
                    id: filter.id,
                    label: filter.label,
                    onRemove: () => handleRemoveFilter(filter),
                  },
                ];
              }
            })
            .map((chip) => (
              <span
                key={chip.id}
                className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1 rounded-md text-sm font-medium flex items-center shadow-sm"
              >
                {chip.label}
                <button
                  className="ml-2 text-blue-400 hover:text-blue-700 focus:outline-none transition-colors"
                  onClick={chip.onRemove}
                >
                  <CloseIcon />
                </button>
              </span>
            ))}
        </div>
      </div>

      {showFilterDropdown &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6"
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
          >
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity cursor-pointer"
              onClick={closePanel}
            ></div>

            <div className="relative w-full max-w-md bg-white sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden transform transition-all duration-200 ease-out">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white shrink-0">
                <div className="flex items-center space-x-3">
                  {selectedFilter && (
                    <button
                      onClick={handleBack}
                      className="text-gray-400 hover:text-gray-800 transition-colors p-1.5 -ml-1.5 rounded-full hover:bg-gray-100"
                    >
                      <ArrowLeft />
                    </button>
                  )}
                  <h2 className="text-lg font-semibold text-gray-800">
                    {!selectedFilter
                      ? "Filter by"
                      : selectedFilter === "campaign"
                        ? "Campaign Name"
                        : selectedFilter === "groupwise"
                          ? "Groups"
                          : columns.find((c) => c.key === selectedFilter)
                              ?.title}
                  </h2>
                </div>
                <button
                  onClick={closePanel}
                  className="text-gray-400 hover:text-gray-700 transition-colors p-1.5 -mr-1.5 rounded-full hover:bg-gray-100"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto bg-slate-50 flex flex-col">
                {!selectedFilter ? (
                  <div className="flex-1 p-5 space-y-6">
                    {(onApplyCampaignFilter || onApplyGroupFilter) && (
                      <div className="space-y-2">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">
                          General
                        </h3>
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          {onApplyCampaignFilter && (
                            <button
                              className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-100 transition-colors"
                              onClick={() => setSelectedFilter("campaign")}
                            >
                              Campaign Name
                            </button>
                          )}
                          {onApplyGroupFilter && (
                            <button
                              className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors"
                              onClick={() => setSelectedFilter("groupwise")}
                            >
                              {currentGroupType || "Campaign"} Groups
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2 pb-2">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">
                        Columns
                      </h3>
                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        {uniqueCategory.map((category) => (
                          <div
                            key={category}
                            className="border-b border-gray-100 last:border-0"
                          >
                            <button
                              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 text-sm font-medium text-gray-800 transition-colors focus:outline-none"
                              onClick={() =>
                                setExpandedCategory(
                                  expandedCategory === category
                                    ? null
                                    : category,
                                )
                              }
                            >
                              {category}{" "}
                              <ChevronDown
                                isOpen={expandedCategory === category}
                              />
                            </button>

                            {expandedCategory === category && (
                              <div className="bg-slate-50/50 px-3 py-2 border-t border-gray-100">
                                {columns
                                  .filter((col) => col.category === category)
                                  .map((col) => (
                                    <button
                                      key={col.key}
                                      className="w-full flex items-center px-4 py-2.5 text-sm text-left text-gray-600 rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors"
                                      onClick={() => setSelectedFilter(col.key)}
                                    >
                                      {col.title}
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col p-6 bg-white">
                    {selectedFilter === "campaign" && (
                      <div className="space-y-4 flex-1">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Contains Text
                          </label>
                          <input
                            type="text"
                            value={campaignNameInput}
                            onChange={(e) =>
                              setCampaignNameInput(e.target.value)
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                            placeholder="Enter campaign name..."
                          />
                        </div>
                      </div>
                    )}

                    {selectedFilter === "groupwise" && (
                      <div className="space-y-4 flex-1">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Select Groups
                          </label>
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                            <FormControl fullWidth size="small">
                              <Select
                                multiple
                                value={selectedGroups}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.includes("all")) {
                                    setSelectedGroups(
                                      selectedGroups.length === groups.length
                                        ? []
                                        : groups.map(
                                            (g) => g.group_name || g.name,
                                          ),
                                    );
                                    return;
                                  }
                                  setSelectedGroups(value);
                                }}
                                displayEmpty
                                renderValue={(selected) =>
                                  selected.length
                                    ? selected.join(", ")
                                    : "Choose groups..."
                                }
                                MenuProps={{ style: { zIndex: 100000 } }}
                              >
                                <MenuItem value="all">
                                  <Checkbox
                                    checked={
                                      groups.length > 0 &&
                                      selectedGroups.length === groups.length
                                    }
                                    indeterminate={
                                      selectedGroups.length > 0 &&
                                      selectedGroups.length < groups.length
                                    }
                                  />
                                  <ListItemText
                                    primary={
                                      selectedGroups.length > 0
                                        ? "Deselect All"
                                        : "Select All"
                                    }
                                    primaryTypographyProps={{
                                      fontWeight: "600",
                                    }}
                                  />
                                </MenuItem>
                                {groups.map((group) => (
                                  <MenuItem
                                    key={group.group_id || group.id}
                                    value={group.group_name || group.name}
                                  >
                                    <Checkbox
                                      checked={selectedGroups.includes(
                                        group.group_name || group.name,
                                      )}
                                    />
                                    <ListItemText
                                      primary={group.group_name || group.name}
                                    />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        </div>
                      </div>
                    )}

                    {columns.some((c) => c.key === selectedFilter) && (
                      <div className="space-y-4 flex-1">
                        {columns
                          .filter((col) => col.key === selectedFilter)
                          .map((col) => {
                            const isNum =
                              col.keyType === "number" ||
                              [
                                "number",
                                "currency",
                                "percentage",
                                "float",
                              ].includes(col.type);
                            const isBool =
                              col.type === "boolean" ||
                              col.keyType === "boolean";
                            const isMultiPercent =
                              col.type === "percentage-multi";

                            let uniqueOptions = [];
                            if (isMultiPercent) {
                              uniqueOptions = PERCENT_MULTI_OPTIONS;
                            } else if (isBool && data) {
                              const rawValues = [];
                              const processVal = (val) => {
                                if (Array.isArray(val)) val.forEach(processVal);
                                else if (
                                  typeof val === "object" &&
                                  val !== null
                                ) {
                                  if (val.value !== undefined)
                                    rawValues.push(val.value);
                                  else if (val.displayProperty)
                                    rawValues.push(val.displayProperty);
                                } else if (val != null) rawValues.push(val);
                              };

                              data.forEach((item) => {
                                processVal(item[col.key]);
                                item.segments?.forEach((seg) =>
                                  processVal(seg[col.key]),
                                );
                              });

                              uniqueOptions = [
                                ...new Set(rawValues.filter((v) => v !== "")),
                              ].sort();
                            }

                            return (
                              <div key={col.key} className="space-y-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                  Configure condition
                                </label>

                                {isBool || isMultiPercent ? (
                                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                    <FormControl fullWidth size="small">
                                      <Select
                                        multiple
                                        value={selectedMultiOptions}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value.includes("all")) {
                                            setSelectedMultiOptions(
                                              selectedMultiOptions.length ===
                                                uniqueOptions.length
                                                ? []
                                                : uniqueOptions.map(String),
                                            );
                                            return;
                                          }
                                          setSelectedMultiOptions(value);
                                        }}
                                        displayEmpty
                                        renderValue={(selected) =>
                                          selected.length
                                            ? selected.join(", ")
                                            : "Select Options..."
                                        }
                                        MenuProps={{
                                          style: { zIndex: 100000 },
                                        }}
                                      >
                                        <MenuItem value="all">
                                          <Checkbox
                                            checked={
                                              uniqueOptions.length > 0 &&
                                              selectedMultiOptions.length ===
                                                uniqueOptions.length
                                            }
                                            indeterminate={
                                              selectedMultiOptions.length > 0 &&
                                              selectedMultiOptions.length <
                                                uniqueOptions.length
                                            }
                                          />
                                          <ListItemText
                                            primary={
                                              selectedMultiOptions.length > 0
                                                ? "Deselect All"
                                                : "Select All"
                                            }
                                            primaryTypographyProps={{
                                              fontWeight: "600",
                                            }}
                                          />
                                        </MenuItem>
                                        {uniqueOptions.map((opt) => (
                                          <MenuItem
                                            key={opt}
                                            value={String(opt)}
                                          >
                                            <Checkbox
                                              checked={selectedMultiOptions.includes(
                                                String(opt),
                                              )}
                                            />
                                            <ListItemText
                                              primary={String(opt)}
                                            />
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </div>
                                ) : isNum ? (
                                  <div className="flex items-center space-x-2">
                                    <select
                                      value={comparison}
                                      onChange={(e) =>
                                        setComparison(e.target.value)
                                      }
                                      className="w-1/3 border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                                    >
                                      <option value=">">Greater (&gt;)</option>
                                      <option value="<">Less (&lt;)</option>
                                      <option value="=">Equals (=)</option>
                                    </select>
                                    <input
                                      type="number"
                                      value={staticFilterInput}
                                      onChange={(e) =>
                                        setStaticFilterInput(e.target.value)
                                      }
                                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                                      placeholder="Enter value"
                                    />
                                  </div>
                                ) : (
                                  <input
                                    type="text"
                                    value={staticFilterInput}
                                    onChange={(e) =>
                                      setStaticFilterInput(e.target.value)
                                    }
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                                    placeholder="Contains text..."
                                  />
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}

                    <div className="flex space-x-3 mt-8 pt-5 border-t border-gray-100">
                      <button
                        onClick={clearFormFields}
                        className="flex-1 bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          if (selectedFilter === "campaign") {
                            onApplyCampaignFilter();
                            closePanel();
                          } else if (selectedFilter === "groupwise") {
                            onApplyGroupFilter();
                            closePanel();
                          } else {
                            const col = columns.find(
                              (c) => c.key === selectedFilter,
                            );
                            if (col) handleStaticFilterApply(col);
                          }
                        }}
                        className="flex-[2] bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Apply Filter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
